import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Row, Col, Card } from "react-bootstrap";
import { IActionHandler, AccountType, WebAuthnKeyAuthenticate } from "@preveil-api";
import {
  GlobalErrorMessages, MessageAnchors, QueryParamKeys, normalizeQueryUserId, useAppDispatch, Message, MessageHandlerDisplayType, useAppSelector, LocalAccountStorage, Account,
  AccountIdentifiers, account_types, LoginWebSteps, StepDisplayNumbers, AccountMessages, AccountErrorMessages, AuthenticationConstants, useVerifyAppStatus, verifyIfDriveLink, ConfigKeys,
  AuthenticationTypes, usePasswordRemoteValidation, useAuthContext
} from "src/common";
import { Loading, ErrorMessage, ProgressCircles, PasswordChangedMessage } from "src/components";
import { LoginEmailForm, LoginPasswordForm, LoginSMSCodeVerificationForm, LoginTOTPCodeVerificationForm, LoginWebAuthKey, ChangePassword } from ".";
import { uiActions, accountActions, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";

function LoginWebComponent() {
  const [search_params, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const auth_token = useAppSelector((state: RootState) => state.account.auth_token);
  const account_ids = useAppSelector((state: RootState) => state.account.account_ids);
  const webauth_info = useAppSelector((state) => state.account.webauth_info);
  const redirect_url = useAppSelector((state: RootState) => state.ui.redirect_url);
  const step = useAppSelector((state: RootState) => state.account.status);
  const [user_id, setUserId] = useState<string>();
  const [password, setPassword] = useState<string>();
  const { verifyDesktopAppIsRunning } = useVerifyAppStatus();
  const { error: invalid_password, validatePassword } = usePasswordRemoteValidation(!!user_id ? { user_id } : undefined);
  const { loginComplete, redirectPath } = useAuthContext();
  const local_app_server = getConfigValue(ConfigKeys.local_app_server);

  // Description: load LoginEmailForm (First onload event)
  useEffect(() => {
    const storage = new LocalAccountStorage();
    !!storage.pv_account ?
      handleGetAccounts(storage) : handleInitWebLogin();
    return () => { // On unmount destroy the KSS store info
      dispatch(accountActions.destroyAccountFormStates());
      dispatch(uiActions.handleMessageDismiss());
    };
  }, []);

  // Description: Complete step after KSS User creds
  useEffect(() => {
    !!account_ids && dispatch(accountActions.findCollectionServerCurrentUser(account_ids));
  }, [account_ids]);

  // Description: Update the login status when current account is set in state
  useEffect(() => {
    const session = LocalAccountStorage.getStorageItem(AuthenticationConstants.ACCOUNT_TYPE_SESSION_KEY);
    const account_type = !!session ? session as AccountType : account_types.express;
    // NOTE: If the password set is not a secured password, ask user to change it
    if (!!current_account || !!redirect_url) {
      !!invalid_password ?
        dispatch(accountActions.setComponentStatus(LoginWebSteps.CHANGE_PASSWORD)) :
        loginComplete(current_account, account_type, redirect_url);
    }
  }, [current_account, redirect_url]);

  /* Description:  Initialize web login following steps above
  * Handles get _user_id from QS or session then gets accounts
  * LOGIN LOGIC AND STEPS:
  * 1. GET user_id from QS - Validate email address -> Check Type on Step 2 (password form loading) 
  * *   - redirect to choose account if Full - SET MODE TO account_types.express
  * *   - set user_id and continue login if express - SET MODE TO account_types.express
  * *   - redirect to create account if null
  * 2. Set Step 1 if no query user and continue normal login flow
  */
  async function handleInitWebLogin() {
    const query_expired = search_params.get(QueryParamKeys.STATUS_CODE);
    const query = search_params.get(QueryParamKeys.USER_ID_QUERY_KEY);
    const query_user_id = normalizeQueryUserId(query);
    !!query_user_id && setUserId(query_user_id);
    // Note: if the redirect path is a drive link, check if the desktop app is running.
    // If it is, redirect to the desktop app. If Desktop app is not running it will return to login page. (Submit Email)
    const is_drive_link = verifyIfDriveLink(redirectPath);
    const is_desktop_app_running = !!is_drive_link && await verifyDesktopAppIsRunning();
    if (!!is_desktop_app_running) {
      window.location.href = `${local_app_server}${redirectPath}`;
      return;
    }

    // NOTE: Handle errors coming from Key Expiration and add others as we develop
    !!query_expired &&
      dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.expired_key_error, MessageHandlerDisplayType.toastr)));

    // NOTE: Remove params
    if (!!search_params.toString()) {
      !!query && search_params.delete(QueryParamKeys.USER_ID_QUERY_KEY);
      !!query_expired && search_params.delete(QueryParamKeys.STATUS_CODE);
      setSearchParams(search_params);
    }
    dispatch(accountActions.setComponentStatus(LoginWebSteps.SUBMIT_EMAIL));
  }

  // Description: Handle Login from sessions
  function handleGetAccounts(storage: LocalAccountStorage) {
    Account.getExpressAccountIdentifiersFromSessions(storage)
      .then((account_identifier: AccountIdentifiers | null) => {
        !!account_identifier ? dispatch(accountActions.findCollectionServerCurrentUser(account_identifier)) :
          LocalAccountStorage.destroyLocalStorageSessions();
      });
  }

  // Description: Handle all children component actions and store it
  const LoginRequests = {
    // Description: Already validated email 
    handleSubmitEmail: (user_email: string) => {
      setUserId(user_email);
      dispatch(accountActions.setComponentStatus(LoginWebSteps.SUBMIT_PASSWORD));
    },
    // Description: Set password or Resend from Verification form
    // Note: for Resend SMS verification click  (pass = undefined)
    handleSubmitPassword: (pass: string) => {
      const _password = !!pass ? pass : password;
      dispatch(accountActions.setComponentStatus(LoginWebSteps.LOADING));
      if (!!_password && !!user_id) {
        validatePassword(_password); // NOTE: Remote validation of current password
        dispatch(accountActions.getKSSUser({ user_id, password: _password }));
        setPassword(_password);
      } else {
        LoginRequests.handlePageError(AccountErrorMessages.login_submit_password);
      }
    },
    handleSubmitSMSVerificationCode: (sms_code: string) => {
      dispatch(accountActions.setComponentStatus(LoginWebSteps.LOADING));
      if (!!user_id && !!password && !!sms_code) {
        dispatch(accountActions.getKSSUser({ user_id, password, sms_code, two_factor_type: AuthenticationTypes.SMS }));
      } else {
        LoginRequests.handlePageError(AccountErrorMessages.bad_sms_code);
      }
    },
    handleSubmitTOTPVerificationCode: (totp_code: string) => {
      dispatch(accountActions.setComponentStatus(LoginWebSteps.LOADING));
      if (!!user_id && !!password && !!totp_code) {
        dispatch(accountActions.getKSSUser({ user_id, password, totp_code, two_factor_type: AuthenticationTypes.TOTP }));
      } else {
        LoginRequests.handlePageError(AccountErrorMessages.bad_totp_code);
      }
    },
    handleSubmitWebAuthnKey: (params: { web_auth_key: WebAuthnKeyAuthenticate, type: AuthenticationTypes }) => {
      const { web_auth_key, type } = params;
      dispatch(accountActions.setComponentStatus(LoginWebSteps.LOADING));
      if (!!web_auth_key && !!user_id && !!password) {
        dispatch(accountActions.getKSSUser({ user_id, password, web_auth_key, two_factor_type: type }));
      } else {
        LoginRequests.handlePageError(AccountErrorMessages.bad_totp_code);
      }
    },
    handleSubmitVerificationCodeError: (message: string) => { // WORKING
      dispatch(uiActions.handleRequestErrors(new Message(message)));
    },
    // Description: Password changed successfully
    handleSubmitPasswordChange: (new_auth_token: string) => {
      dispatch(uiActions.handleSetMessage(new Message(AccountMessages.password_updated_success)));
      dispatch(accountActions.setComponentStatus(LoginWebSteps.CHANGE_PASSWORD_SUCCESS));
      dispatch(accountActions.handleSetAuthToken(new_auth_token));
    },
    handlePasswordChangeSuccess: () => {
      !!current_account ?
        loginComplete(current_account, account_types.express, redirect_url) : LoginRequests.handleResetLogin();
    },
    // Description: Reset all form states and go back to first step
    handleResetLogin: () => {
      setUserId(undefined);
      setPassword(undefined);
      dispatch(accountActions.setComponentStatus(LoginWebSteps.SUBMIT_EMAIL));// setStep(1);
    },
    handlePageError: (message: string) => {
      LoginRequests.handleResetLogin();
      dispatch(uiActions.handleRequestErrors(new Message(message)));
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    },
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    dispatch(uiActions.handleMessageDismiss());
    const callback = `handle${action_obj.actionType}`;
    // Handle local calls:
    if ((LoginRequests as any)[callback] instanceof Function) {
      (LoginRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      LoginRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  // Description: Render the correct form
  function LoginForms() {
    switch (step) {
      case 1:
        return <LoginEmailForm handleAction={handlePageActions} user_id={user_id}><>
          <h3 className="border-bottom">Login to Your Account</h3>
          <p>Please enter your <b>PreVeil Express user ID</b>, this is also your regular email address.</p>
        </>
        </LoginEmailForm>;
      case 2:
        return !!user_id ?
          <LoginPasswordForm handleAction={handlePageActions} user_id={user_id} is_login={true}>
            <h3 className="border-bottom">Enter your PreVeil Express password</h3>
          </LoginPasswordForm> :
          <ErrorMessage message={GlobalErrorMessages.default} />;
      case 3:
        return <LoginSMSCodeVerificationForm handleAction={handlePageActions} />;
      case 4:
        return <LoginTOTPCodeVerificationForm handleAction={handlePageActions} />;
      case 5:
        return !!webauth_info ? <LoginWebAuthKey handleAction={handlePageActions} webauth_info={webauth_info} /> : <ErrorMessage message={GlobalErrorMessages.default} />;
      case 6: // LoginWebSteps.CHANGE_PASSWORD
        return (!!current_account && !!auth_token) ?
          <ChangePassword current_account={current_account} auth_token={auth_token} handleAction={handlePageActions} /> :
          <ErrorMessage message={GlobalErrorMessages.default} />;
      case 7: // LoginWebSteps.CHANGE_PASSWORD_SUCCESS
        return <PasswordChangedMessage handleClose={LoginRequests.handlePasswordChangeSuccess} />;
      default:
        return <Loading className="in-place" />;
    }
  }

  return step > 0 ? <Row className="justify-content-center">
    <Col md={7} className="max-500 mt-5">
      <Card>
        {
          StepDisplayNumbers[step] > 0 &&
          <ProgressCircles total_steps={4} current_step={StepDisplayNumbers[step]} />
        }
        <Card.Body className="p-3">
          <LoginForms />
        </Card.Body>
      </Card>
    </Col>
  </Row> : <Loading />;
};

export default React.memo(LoginWebComponent);
